import {
  DistributionNavState,
  DistributionState,
  LiquidityState,
  PricingState,
  ToggleOptionWithPeriod,
} from '@types';

export const dateRangeToggleInitialValues: Map<
  string,
  ToggleOptionWithPeriod
> = new Map([
  [
    'ONE_MONTH',
    {
      active: true,
      value: 'ONE_MONTH',
      text: 'products.price-toggle-1-month',
      period: {
        key: 'months',
        value: 1,
      },
    },
  ],
  [
    'THREE_MONTHS',
    {
      active: false,
      value: 'THREE_MONTHS',
      text: 'products.price-toggle-3-month',
      period: {
        key: 'months',
        value: 3,
      },
    },
  ],
  [
    'SIX_MONTHS',
    {
      active: false,
      value: 'SIX_MONTHS',
      text: 'products.price-toggle-6-month',
      period: {
        key: 'months',
        value: 6,
      },
    },
  ],
  [
    'YTD',
    {
      active: false,
      value: 'YTD',
      text: 'products.price-toggle-ytd',
    },
  ],
  [
    'ONE_YEAR',
    {
      active: false,
      value: 'ONE_YEAR',
      text: 'products.price-toggle-1-year',
      period: {
        key: 'years',
        value: 1,
      },
    },
  ],
  [
    'THREE_YEARS',
    {
      active: false,
      value: 'THREE_YEARS',
      text: 'products.price-toggle-3-years',
      period: {
        key: 'years',
        value: 3,
      },
    },
  ],
  [
    'FIVE_YEARS',
    {
      active: false,
      value: 'FIVE_YEARS',
      text: 'products.price-toggle-5-years',
      period: {
        key: 'years',
        value: 5,
      },
    },
  ],
  [
    'SINCE_INCEPTION',
    {
      active: false,
      value: 'SINCE_INCEPTION',
      text: 'products.price-toggle-since-inception',
    },
  ],
  [
    'CUSTOM_RANGE',
    {
      active: false,
      value: 'CUSTOM_RANGE',
      text: 'custom-range',
    },
  ],
]);

export const premiumDiscountDateRangeToggleInitialValues = (): Map<
  string,
  ToggleOptionWithPeriod
> => {
  const arr = Array.from(dateRangeToggleInitialValues);
  arr.splice(2, 0, [
    'LAST_QUARTER',
    {
      active: false,
      value: 'LAST_QUARTER',
      text: 'products.price-toggle-last-quarter',
    },
  ]);
  arr.splice(5, 0, [
    'LAST_CALENDAR_YEAR',
    {
      active: false,
      value: 'LAST_CALENDAR_YEAR',
      text: 'products.price-toggle-last-calendar-year',
    },
  ]);
  return new Map(arr);
};

export const PRICING_INITIAL_STATE: PricingState = {
  overviewData: {
    shareClass: null,
    nav: null,
    fundName: null,
    salesChargeBreakPoints: null,
  },
  historicalData: [],
  pricingConfig: {
    productType: null,
    isSecondaryCalcTypeAvailable: false,
    suppressNavChangePercentage: false,
    dateValues: {
      incDate: null,
      incDateStd: null,
      asOfDateStd: null,
    },
    showPop: false,
    showMarketPriceData: false,
    showMoneyMarketPriceData: false,
    showPremiumDiscountData: false,
    showApplicationPrice: false,
    showRedemptionPrice: false,
    showForETF: false,
    showMoneyMarketNetFlowColumn: false,
    showMoneyMarketMasterNetFlowColumn: false,
    showMasterFundNav: false,
    showDailyLiquidAssetRatio: false,
    showWeeklyLiquidAssetRatio: false,
    showNetShareHolderFlow: false,
    hidePricingChart: false,
  },
  siteConfig: null,
  chartConfig: {
    chartTitle: null,
    proximalPlacementTop: null,
    proximalPlacementBottom: null,
    caveatPlacement: null,
    tooltip: null,
    dateRangeEnum: dateRangeToggleInitialValues,
    selectedToggleRange: 'SINCE_INCEPTION',
    customRange: {
      fromDateStd: null,
      toDateStd: null,
      fromDate: null,
      toDate: null,
    },
    highChartOptions: null,
  },
  highLowNavTableData: [],
  premiumDiscountHistory: null,
  tableConfig: {
    dateRangeEnum: dateRangeToggleInitialValues,
    selectedToggleRange: 'ONE_MONTH',
    customRange: {
      fromDateStd: null,
      toDateStd: null,
    },
    highestNav: null,
    lowestNav: null,
    highestNavDate: null,
    lowestNavDate: null,
    highestMarketPrice: null,
    lowestMarketPrice: null,
    highestMarketPriceDate: null,
    lowestMarketPriceDate: null,
  },
  isSiteIntl: null,
};

export const DISTRIBUTION_INITIAL_STATE: DistributionState = {
  fundName: null,
  historicalData: [],
  siteConfig: null,
  latestConfig: {
    asOfDate: null,
    isSingleShareClass: null,
    shareClassName: null,
    shareClassInceptionDate: null,
    shareClassInceptionDateStd: null,
    currencyCode: null,
    incomeRecordDate: null,
    incomeExDividendDate: null,
    incomeDividendDate: null,
    navValue: null,
    incomeDistributionAmount: null,
    divDistributionIncome: null,
    dividendCapital: null,
    isSgHk: false,
    totalDistributionAmountMonthly: null,
    showCanadaDistForEtfMfMmf: false,
    capitalGains: null,
    hideExDistDate: false,
  },
  tableConfig: {
    dividendYears: [],
    selectedYear: null,
    startDate: null,
    endDate: null,
  },
  isSiteIntl: null,
  latestDistributions: [],
  highChartOptions: null,
};

export const DISTRIBUTION_NAV_INITIAL_STATE: DistributionNavState = {
  fundName: null,
  historicalData: [],
  pricingHistoricalData: [],
  siteConfig: null,
  latestConfig: {
    asOfDate: null,
    isSingleShareClass: null,
    shareClassName: null,
    shareClassInceptionDate: null,
    shareClassInceptionDateStd: null,
    currencyCode: null,
    incomeRecordDate: null,
    incomeExDividendDate: null,
    incomeDividendDate: null,
    navValue: null,
    incomeDistributionAmount: null,
    divDistributionIncome: null,
    dividendCapital: null,
    isSgHk: false,
    totalDistributionAmountMonthly: null,
    showCanadaDistForEtfMfMmf: false,
    capitalGains: null,
    hideExDistDate: false,
  },
  tableConfig: {
    dividendYears: [],
    selectedYear: null,
    startDate: null,
    endDate: null,
  },
  isSiteIntl: null,
  latestDistributions: [],
  highChartOptions: null,
};

export const LIQUIDITY_INITIAL_STATE: LiquidityState = {
  historicalData: [],
  chartConfig: {
    chartTitle: null,
    proximalPlacementTop: null,
    proximalPlacementBottom: null,
    caveatPlacement: null,
    tooltip: null,
    dateRangeEnum: dateRangeToggleInitialValues,
    selectedToggleRange: 'SIX_MONTHS',
    customRange: {
      fromDateStd: null,
      toDateStd: null,
      fromDate: null,
      toDate: null,
    },
    highChartOptions: null,
  },
  asOfDate: null,
  hasLoadedFullFeed: false,
};
